import { DebugModalComponent } from '@/components/debug-modal/debug-modal.component';
import { AdvizeoUiElementSize } from '@/interfaces/AdvizeoUi.interface';
import { SsoAuthenticationSessionStorageKey } from '@/libs/rosa/lib/core/constants/authentication-session-storage-key.enum';
import { RosaCoreLocalStorageKey } from '@/rosa';
import { AdvizeoModalService } from '@/services/advizeo-modal.service';
import { DisabledFeaturesService } from '@/utils/disabled-features/disabled-features.service';
import { Language } from '@/utils/i18n/language.interface';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Location } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, filter, firstValueFrom, type Observable } from 'rxjs';
import { DOMAIN } from '../../constants/cookies';
import { BrandingProvider } from '../branding/branding.provider';
import { BrowserProvider } from '../browser.provider';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from '../local-storage.contants';

const QUERY_PARAMS_FUNC = [
    { name: 'refresh-token', store: (value: string) => localStorage.setItem(RosaCoreLocalStorageKey.REFRESH_TOKEN, value) },
    { name: 'dashboard', store: (value: string) => sessionStorage.setItem(SESSION_STORAGE_KEYS.SELECTED_DASHBOARD, value) },
    { name: 'node-id', store: (value: string) => localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_NODE_ID, value) },
    { name: 'period', store: (value: string) => sessionStorage.setItem(SESSION_STORAGE_KEYS.SELECTED_PERIOD, value) },
    {
        name: 'print',
        store: (value: string) => sessionStorage.setItem(SESSION_STORAGE_KEYS.REPORT_PRINT_SESSION, `${value !== 'false'}`)
    },
    { name: 'ignore-estimated', store: (value: string) => localStorage.setItem(LOCAL_STORAGE_KEYS.IGNORE_ESTIMATED_ON_WIDGETS, value) }
];

@Injectable({
    providedIn: 'root'
})
export class AppInitializationProvider {
    private _disabledFeaturesService = inject(DisabledFeaturesService);
    private _brandingProvider = inject(BrandingProvider);
    private _cookieService = inject(CookieService);
    private _browserProvider = inject(BrowserProvider);
    private _modalService = inject(AdvizeoModalService);
    private _translateService = inject(TranslateService);
    private _location = inject(Location);

    async checkBrowserCompatibility(): Promise<void> {
        if (!this.isNavigatorCompatible()) {
            document.location.replace('/assets/static-pages/compatibility.html');
            return;
        }
    }

    private _isAppInitialized$ = new BehaviorSubject(false);
    private _isSsoWorkflowInProgress = signal(true);

    readonly isSsoWorkflowInProgress = this._isSsoWorkflowInProgress.asReadonly();

    notifyAppInitialization() {
        this._isAppInitialized$.next(true);
    }

    isAppInitialized$(): Observable<boolean> {
        return this._isAppInitialized$.asObservable();
    }

    isNavigatorCompatible(): boolean {
        const userAgent = navigator.userAgent;
        // Base on https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Browser_detection_using_the_user_agent#browser_name_and_version
        const userAgentsCompatibleName = ['Chrome', 'Firefox', 'Safari', 'OPR'];
        return userAgentsCompatibleName.some(userAgentName => userAgent.includes(userAgentName));
    }

    /**
     * Checks if there is a cookie that references another domain.
     * This could happen if the user was redirected to the platform from my.advizeo
     */
    async checkUrlCoherenceWithCookies() {
        const domain = this._cookieService.get(DOMAIN);
        if (domain) {
            this._cookieService.delete(DOMAIN);
            this._browserProvider.goTo('http://' + domain, { newTab: false });
            return;
        }
    }

    async initializeApp(): Promise<void> {
        await this._disabledFeaturesService.initialize();
        this.initializeLocalStorageFromURLParams();
        this.initializeKeyboardShortcuts();
        await this.prefetchDataAtAppStartup();
        await this.handleSsoWorkflow();
    }

    private initializeLocalStorageFromURLParams() {
        const url = new URL(window.location.href);
        if (url.search.length > 0) {
            QUERY_PARAMS_FUNC.forEach(queryParam => {
                const paramValue = url.searchParams.get(queryParam.name);
                if (paramValue) {
                    queryParam.store(paramValue);
                    url.searchParams.delete(queryParam.name);
                }
            });
            if (window.location.href !== url.href) {
                window.location.href = url.href;
            }
        }
    }

    private prefetchDataAtAppStartup() {
        return this._brandingProvider.fetchBranding(window.location.hostname);
    }

    private async handleSsoWorkflow(): Promise<void> {
        return firstValueFrom(this._brandingProvider.getBranding$().pipe(filter(isNotNullish)))
            .then(async branding => {
                const connectedWithSso = localStorage.getItem(RosaCoreLocalStorageKey.CONNECTED_WITH_SSO);

                // If the user is already connected with sso => call the IDP to ensure the user still exists
                if (connectedWithSso) {
                    localStorage.removeItem(RosaCoreLocalStorageKey.CONNECTED_WITH_SSO);

                    sessionStorage.setItem(
                        SsoAuthenticationSessionStorageKey.URL_BEFORE_IDP_REDIRECTION,
                        window.location.pathname + window.location.search
                    );
                    this._brandingProvider.connectWithSso();
                    return new Promise<never>(() => {}); // avoid unwanted effect after redirection
                }

                // If there is an IDP token in the URL fragment, store it to use in auto-login
                if (
                    this._brandingProvider.isSsoAvailable(branding) &&
                    sessionStorage.getItem(SESSION_STORAGE_KEYS.REPORT_PRINT_SESSION) !== 'true'
                ) {
                    const fragmentParams = new URLSearchParams(window.location.hash.substring(1));
                    const idpToken = fragmentParams.get('access_token');

                    const searchParams = new URLSearchParams(window.location.search);
                    const idpCode = searchParams.get('code');

                    if (idpToken || idpCode) {
                        if (idpToken) {
                            this.checkState(fragmentParams.get('state'));
                            sessionStorage.setItem(SsoAuthenticationSessionStorageKey.IDP_TOKEN, idpToken);
                        } else if (idpCode) {
                            this.checkState(searchParams.get('state'));
                            sessionStorage.setItem(SsoAuthenticationSessionStorageKey.IDP_CODE, idpCode);
                        }

                        const urlBeforeIdpRedirection = sessionStorage.getItem(
                            SsoAuthenticationSessionStorageKey.URL_BEFORE_IDP_REDIRECTION
                        );
                        sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.URL_BEFORE_IDP_REDIRECTION);
                        this._location.go(urlBeforeIdpRedirection ?? '/');
                    } else if (this._brandingProvider.shouldAutoLogin(branding)) {
                        this._brandingProvider.connectWithSso();
                        return new Promise<never>(() => {}); // avoid unwanted effect after redirection
                    }
                }
            })
            .catch(err => {
                this.setFailureMessageInBootLoader();
                this.setBootSpinnerVisibility(false);
                throw err;
            })
            .finally(() => {
                this._isSsoWorkflowInProgress.set(false);
            });
    }

    private initializeKeyboardShortcuts() {
        document.addEventListener('keyup', event => {
            if (
                (event.key === 'e' || event.key === 'i' || event.code === 'KeyE' || event.code === 'KeyI') &&
                event.ctrlKey &&
                event.altKey
            ) {
                this._modalService.openAdvizeoModal(DebugModalComponent, null, { size: AdvizeoUiElementSize.LARGE });
            }
        });

        // This shortcut is meant to be used by translators to display translations keys instead of translations in the app
        document.addEventListener('keyup', event => {
            if ((event.key === 'l' || event.code === 'KeyL') && event.ctrlKey && event.altKey) {
                const isAlreadyInNoneLang = this._translateService.getCurrentLang() === 'none';
                this._translateService.setFallbackLang(isAlreadyInNoneLang ? Language.EN : 'none');
                this._translateService.use(isAlreadyInNoneLang ? Language.FR : 'none');
            }
        });
    }

    // These methods are executed before the app is booted, so we need to access the html directly

    setFailureMessageInBootLoader() {
        this.setBootLoadingMessage(`Le chargement de l'application a échoué.<br />Essayez de rafraîchir la page.`);
    }

    setBootLoadingMessage(message: string) {
        const bootLoadingBlock = document.querySelector('#boot-loading-text');
        if (bootLoadingBlock) {
            bootLoadingBlock.innerHTML = message;
        }
    }

    setBootSpinnerVisibility(isVisible: boolean) {
        document.querySelector('#boot-loading-spinner')?.setAttribute('style', `display: ${isVisible ? 'inline-block' : 'none'};`);
    }

    private checkState(state: string | null) {
        const storedState = sessionStorage.getItem(SsoAuthenticationSessionStorageKey.STATE_CLAIM);
        sessionStorage.removeItem(SsoAuthenticationSessionStorageKey.STATE_CLAIM);

        if (state == null || state != storedState) {
            throw Error('Auth Failed: state mismatch');
        }
    }
}
